import $i18n from '@/locales/i18n';
import isPlainObject from 'lodash/isPlainObject';
import Icon from './logo';
import { createBlockInitialChildrenSnippet, createBlockSnippet, createEmptyBlockSnippet, createSectionInitialChildren, getObjectSetterSnippet, } from '@/common/manager/setter-snippet';
import { BLOCK } from '@/common/util/const';
import { updateSpan } from '@/lowcode/cn-layout-cell/components/split/auto-block';
import LayoutSetter from '@/common/setter/layout-setter/index';
import UpdateSetter from '@/common/setter/update-setter';
import { replaceNode } from '@/common/util/block';
function handleI18nLabel(label) {
    if (isPlainObject(label) && label.type === 'cn-i18n') {
        return label.dm || '';
    }
    return label;
}
const CnLayoutSectionPrototype = {
    title: $i18n.get({ id: 'FreeLayoutContainer', dm: '自由布局容器' }),
    componentName: 'CnLayoutSection',
    category: $i18n.get({ id: 'Advanced', dm: '高级' }),
    icon: Icon,
    docUrl: '',
    isContainer: true,
    initialChildren: createSectionInitialChildren,
    // 为直接拖拽进CnLayoutSection的非CnLayoutBlock组件添加块结构
    didDropIn: (dropInNode, targetNode) => {
        if (dropInNode?.componentName === 'CnLayoutBlock' ||
            dropInNode?.parent?.id !== targetNode?.id) {
            return;
        }
        const newSchema = dropInNode?.componentName === 'CnCard'
            ? createEmptyBlockSnippet({
                blockProps: { span: 12 },
                blockChildren: dropInNode.schema,
            })
            : createBlockSnippet({
                cardProps: {
                    noPadding: true,
                    noShadow: true,
                },
                cardChildren: dropInNode.schema,
            });
        try {
            replaceNode(dropInNode, newSchema);
        }
        catch (e) {
            // FIXME: 编辑器初始化时会触发didDropIn，此时的dropInNode数据结构不完整
        }
    },
    didDropOut(removedNode) {
        updateSpan({
            parent: removedNode.parent,
            child: removedNode,
            type: 'delete',
        });
    },
    snippets: [
        {
            screenshot: 'https://img.alicdn.com/imgextra/i4/O1CN01lrzpI11ueRo9jGONo_!!6000000006062-0-tps-2048-674.jpg',
            label: 'CnLayoutSection',
            schema: {
                componentName: 'CnLayoutSection',
                props: {},
            },
        },
    ],
    configure: [
        {
            name: 'isCnLayoutSection',
            title: 'CnLayoutSection',
            initialValue: true,
            display: 'none',
        },
        {
            name: '_context',
            title: $i18n.get({ id: 'Context', dm: '上下文' }),
            initialValue: {
                type: 'JSExpression',
                value: 'this',
            },
            display: 'none',
        },
        {
            title: $i18n.get({ id: 'Layout', dm: '布局' }),
            name: '_layout',
            display: 'accordion',
            onecodeDisplay: 'visible',
            collapsed: false,
            setter: getObjectSetterSnippet({
                items: [
                    {
                        name: 'layout',
                        display: 'plain',
                        defaultValue: '6:6',
                        extraProps: {
                            getValue(target) {
                                const node = target?.getNode?.();
                                const children = node?.getChildren?.();
                                const list = children?.map?.((child) => child?.getPropValue?.('span'));
                                return list?.join?.(':');
                            },
                            setValue(target, value) {
                                const size = value?.split(':').length;
                                const list = value?.split(':');
                                const node = target?.getNode?.();
                                node.mergeChildren((child, index) => {
                                    child.setPropValue('span', +list?.[index] || 1);
                                    return index >= size;
                                }, (children) => {
                                    let l = children.length;
                                    const items = [];
                                    while (l++ < size) {
                                        items.push({
                                            componentName: BLOCK,
                                            props: {
                                                span: +list?.[l - 1] || 1,
                                            },
                                            children: [createBlockInitialChildrenSnippet({})],
                                        });
                                    }
                                    return items;
                                }, null);
                            },
                        },
                        setter: <LayoutSetter />,
                    },
                ],
            }),
        },
        {
            setter: (target) => {
                return <UpdateSetter target={target}/>;
            },
            extraProps: {
                condition: (target) => {
                    const title = handleI18nLabel(target?.parent?.getPropValue('normalStyle')?.title);
                    const componentSlot = target?.parent?.getPropValue('_layout')?.componentSlot;
                    const extra = target?.parent?.getPropValue('extra');
                    return !!(title || componentSlot || extra);
                },
            },
        },
        {
            title: $i18n.get({ id: 'Style', dm: '样式' }),
            name: 'normalStyle',
            display: 'accordion',
            collapsed: false,
            setter: getObjectSetterSnippet({
                items: [
                    {
                        name: 'noPadding',
                        title: $i18n.get({ id: 'CloseInsideMargin', dm: '关闭内边距' }),
                        display: 'inline',
                        setter: 'BoolSetter',
                        defaultValue: true,
                    },
                ],
            }),
        },
        {
            title: $i18n.get({ id: 'AdvancedConfiguration', dm: '高级配置' }),
            type: 'group',
            collapsed: true,
            display: 'accordion',
            items: [
                {
                    name: 'handleProps',
                    title: $i18n.get({ id: 'SetComponentPropsManually', dm: '手动设置组件props' }),
                    display: 'inline',
                    setter: {
                        title: $i18n.get({ id: 'WritingJsExpressions', dm: '写js表达式' }),
                        componentName: 'CnRowDataSetter',
                        props: () => {
                            return {
                                list: [],
                                /* i18n-disable-next-line */
                                initialCode: "(oldProps, state) =>{\n// 给组件设置新的props。\n// oldProps.xxx = 'xxx';\n// return oldProps;\n                }",
                                tip: $i18n.get({
                                    id: 'OldPropsTheOriginalOldPropsOfThe_2116961506',
                                    dm: 'oldProps: 组件的原始oldProps\nstate：全部的数据，在左侧列表中选择使用。',
                                }),
                            };
                        },
                    },
                },
            ],
        },
    ],
};
// 原型配置请参考：https://lark.alipay.com/vision/docs/prototype
export { CnLayoutSectionPrototype };
